import { httpsCallable } from "firebase/functions";
import { getIdToken } from "firebase/auth";
import { getToken } from "firebase/app-check";
import { appCheck, auth, cloudFunctions } from "../config/firebase";
import type { CreatorVerificationProof, ReactiveWorldsMapPackage } from "../types";

export interface CreatorVerificationAttestation {
  verificationId: string;
  revisionHash: string;
  verifiedAt: string;
  physicsVersion: string;
  authority: "server" | "local";
  verifierVersion?: string;
  verifiedFrames?: number;
}

async function refreshCallableTokens(forceRefresh = false) {
  if (auth) {
    await auth.authStateReady();
    if (!auth.currentUser)
      throw new Error("Your sign-in session is not ready. Sign in again and retry.");
    await getIdToken(auth.currentUser, forceRefresh);
  }
  if (appCheck) await getToken(appCheck, forceRefresh);
}

async function callCreatorVerification<T>(
  name: string,
  data: Record<string, unknown>,
): Promise<T> {
  if (!cloudFunctions)
    throw new Error("Server-authoritative verification is unavailable.");
  await refreshCallableTokens();
  const callable = httpsCallable<Record<string, unknown>, T>(cloudFunctions, name);
  try {
    return (await callable(data)).data;
  } catch (error) {
    if ((error as { code?: string })?.code !== "functions/unauthenticated") throw error;
    await refreshCallableTokens(true);
    return (await callable(data)).data;
  }
}

export async function attestCreatorCompletion(
  projectId: string,
  pkg: ReactiveWorldsMapPackage,
  proof: CreatorVerificationProof,
): Promise<CreatorVerificationAttestation | null> {
  if (!cloudFunctions) return null;
  return callCreatorVerification<CreatorVerificationAttestation>("recordCreatorCompletion", {
    projectId,
    packageJson: JSON.stringify(pkg),
    proof,
  });
}

export async function publishVerifiedMapDocument(input: {
  mapId: string;
  verificationId: string;
  packageJson: string;
  thumbnailDataUrl: string;
  authorName: string;
  title: string;
  description: string;
  tags: string[];
  isPublic: boolean;
}) {
  if (!cloudFunctions) throw new Error("Server-authoritative publishing is unavailable.");
  return callCreatorVerification<{ mapId: string; revisionHash: string }>(
    "publishVerifiedMap",
    input,
  );
}
